/**
 * extra.json keyframe evaluation (W1-08; formats-and-packaging.md P§1.3, P§1.5, P§5.1), shared by effect uniforms
 * and video alpha / dim:
 *
 *  - every keyframe item pushes (sec(startTime), start, its tween) and (sec(endTime), end, hold), in list order;
 *  - the tween: RPE easing number (<= 0 -> linear, >= 30 -> linear), with the clamped slice applied whenever
 *    easingLeft / easingRight are not the default 0 / 1 (within 1e-5) - unclamped, no linear shortcut
 *    (`resolveExtra`);
 *  - evaluation like Phira's Anim during playback: its cursor only walks forward, past keyframe k once every
 *    keyframe 1..k has a time <= t. With R_k = max(t_1 .. t_k) the cursor at t is the last k with R_k <= t (0 before
 *    that, so the first segment EXTRAPOLATES). For keyframes in time order this is "the last keyframe at or before
 *    t"; for unsorted lists it is what Phira shows when the chart plays forward (a later item that starts earlier
 *    does not take over before every earlier keyframe has passed). Values interpolate per component in f32
 *    (`fround(a + fround(fround(b - a) * y))`), the last value holds;
 *  - values: floats as f32, vec2 per component, colours [r, g, b, a] / 255 in f32;
 *  - video alpha / dim get (0 s, default, hold) prepended so they do not extrapolate (default 1 for alpha, 0 for
 *    dim). Phira prepends it when the first item does not start at beat 0 of the extra.json's OWN BPM list; the IR
 *    holds chart beats (formats/extra/decode.ts converts them when the lists differ), so the engine decides by
 *    seconds: prepend when the first item starts after 0 s. With the forward cursor this shows exactly what Phira
 *    shows at every chart time >= 0 (Phira never plays negative times): beat 0 of a list that starts at beat 0 is
 *    0 s, an item starting before 0 s is passed before any visible time either way, and one starting at 0 s
 *    overrides the default keyframe at the same time. The one case it cannot express (an item at beat 0 of a BPM
 *    list that starts BEFORE beat 0, so after 0 s) is reported by the decoder.
 */

import { resolveExtra } from '../../ease/rpeMap';
import { Ease } from '../../ease';
import type { PhiraTweenDesc } from '../../ease/types';
import { Search } from '../../math/search';
import type { Sec, Triple } from '../../time/types';
import type { IrExtraKeyframe } from '../../ir/types';
import type { RGBA8, Vec2 } from '../../util/types';

const fr = Math.fround;

export type ExtraValue = number | Vec2 | RGBA8;
/** What the shader sees: a float, or a vec2 / vec4 of floats. */
export type UniformValue = number | readonly number[];
export type ExtraValueKind = 'float' | 'vec2' | 'color';

/** The f32 shader value of a written value. */
export function uniformOf(v: ExtraValue, kind: ExtraValueKind): UniformValue {
  if (typeof v === 'number') return fr(v);
  if (kind === 'color') return v.map((c) => fr(c / 255));
  return v.map((c) => fr(c));
}

/** Kind of a value shape (a 2-tuple is a vec2, a 4-tuple a colour). */
export function kindOf(v: ExtraValue): ExtraValueKind {
  return typeof v === 'number' ? 'float' : v.length === 2 ? 'vec2' : 'color';
}

/** A compiled keyframe track. */
export class ExtraAnim {
  readonly size: number;
  private readonly times: Float64Array;
  private readonly values: Float32Array;
  /** Tween of each keyframe toward the next; null = hold. */
  private readonly tweens: readonly (PhiraTweenDesc | null)[];
  /** reach[0] = -inf, reach[k] = max(times[1..k]): the cursor passes keyframe k once t >= reach[k]. */
  private readonly reach: Float64Array;

  private constructor(size: number, times: number[], values: number[], tweens: (PhiraTweenDesc | null)[]) {
    this.size = size;
    this.times = Float64Array.from(times);
    this.values = Float32Array.from(values);
    this.tweens = tweens;
    const reach = new Float64Array(times.length);
    let r = Number.NEGATIVE_INFINITY;
    for (let k = 0; k < times.length; k++) {
      // A NaN time never stops Phira's walk (its "time > t" check is false), so it does not raise the reach.
      const tk = times[k] ?? Number.NaN;
      if (k > 0 && !Number.isNaN(tk)) r = Math.max(r, tk);
      reach[k] = r;
    }
    this.reach = reach;
  }

  /**
   * Compiles keyframe items. `def` (videos) prepends (0 s, def, hold) when the first item starts after 0 s (module
   * header). Returns null for an empty list (Phira panics on it; the decoder never produces one).
   */
  static compile(kfs: readonly IrExtraKeyframe<ExtraValue>[], sec: (t: Triple) => Sec, def?: number): ExtraAnim | null {
    const first = kfs[0];
    if (first === undefined) return null;
    const kind = kindOf(first.start);
    const size = kind === 'float' ? 1 : kind === 'vec2' ? 2 : 4;
    const times: number[] = [];
    const values: number[] = [];
    const tweens: (PhiraTweenDesc | null)[] = [];
    const push = (t: number, v: UniformValue, tween: PhiraTweenDesc | null): void => {
      times.push(t);
      if (typeof v === 'number') values.push(v);
      else for (let c = 0; c < size; c++) values.push(v[c] ?? 0);
      tweens.push(tween);
    };
    if (def !== undefined && sec(first.startTime) > 0) push(0, fr(def), null);
    for (const kf of kfs) {
      push(
        sec(kf.startTime),
        uniformOf(kf.start, kind),
        resolveExtra(kf.easingType, kf.easingLeft ?? 0, kf.easingRight ?? 1),
      );
      push(sec(kf.endTime), uniformOf(kf.end, kind), null);
    }
    return new ExtraAnim(size, times, values, tweens);
  }

  /** Index of the active keyframe at t in forward play (the last k with reach[k] <= t; 0 before that). */
  cursorAt(t: Sec): number {
    const i = Search.upperBound(this.reach, t) - 1;
    return i < 0 ? 0 : i;
  }

  /** Component c of the value at t. */
  componentAt(t: Sec, c: number): number {
    const i = this.cursorAt(t);
    const s = this.size;
    const a = this.values[i * s + c] ?? 0;
    if (i + 1 >= this.times.length) return a;
    const b = this.values[(i + 1) * s + c] ?? 0;
    const tween = this.tweens[i] ?? null;
    const t1 = this.times[i] ?? 0;
    const t2 = this.times[i + 1] ?? 0;
    const y = tween === null ? 0 : Ease.phiraY(tween, fr((t - t1) / (t2 - t1)));
    return fr(a + fr(fr(b - a) * y));
  }

  valueAt(t: Sec): UniformValue {
    if (this.size === 1) return this.componentAt(t, 0);
    const out: number[] = [];
    for (let c = 0; c < this.size; c++) out.push(this.componentAt(t, c));
    return out;
  }
}

/** A variable as the engine evaluates it: fixed, keyframed, or Phira's empty "Default" anim (null). */
export type CompiledVar =
  | { readonly kind: 'none' }
  | { readonly kind: 'fixed'; readonly value: UniformValue }
  | { readonly kind: 'anim'; readonly anim: ExtraAnim };

export function compileVar(
  v: null | ExtraValue | readonly IrExtraKeyframe<ExtraValue>[],
  sec: (t: Triple) => Sec,
  def?: number,
): CompiledVar {
  if (v === null) return { kind: 'none' };
  if (typeof v === 'number') return { kind: 'fixed', value: fr(v) };
  const list = v as readonly unknown[];
  if (list.length > 0 && typeof list[0] === 'object') {
    const anim = ExtraAnim.compile(v as readonly IrExtraKeyframe<ExtraValue>[], sec, def);
    return anim === null ? { kind: 'none' } : { kind: 'anim', anim };
  }
  const fixed = v as Vec2 | RGBA8;
  return { kind: 'fixed', value: uniformOf(fixed, kindOf(fixed)) };
}

/** Value of a compiled variable at t; `none` -> `fallback`. */
export function varAt(v: CompiledVar, t: Sec, fallback: UniformValue): UniformValue {
  switch (v.kind) {
    case 'none':
      return fallback;
    case 'fixed':
      return v.value;
    case 'anim':
      return v.anim.valueAt(t);
  }
}
